import { Server } from 'lucide-react';
import { Card, NotificationCard } from '@afrilink/ui';
import { RESELLER_SUBSTRATE } from '@/config/resellerSubstrate';

/**
 * Co-location decision financial model — static info card on AdminServerHealthPage.
 *
 * Numbers are 2026-05-13 desk-research estimates. The component renders a
 * yellow "estimates pending re-quote" banner until `coloEstimates.verifiedAt`
 * is set to an ISO date (in `resellerSubstrate.ts`), at which point the
 * banner is replaced with a green "verified <date>" footer.
 *
 * **Do NOT use these numbers for actual purchase decisions until quotes signed.**
 */
export function ColoFinancialModel() {
  const co = RESELLER_SUBSTRATE.coloEstimates;
  const verified = co.verifiedAt;
  const isPending = !verified;

  const fmt = (n: number) => `R${n.toLocaleString('en-ZA')}`;

  // Capex payback against XL reseller R3399 — opex saving per month
  const xlReseller = RESELLER_SUBSTRATE.upgradePaths.find((p) => p.name === 'reseller_xl');
  const opexSavingZar = xlReseller ? xlReseller.costZarMonthly - co.opex.totalZarMonthly : 0;
  const capexPaybackMonths = opexSavingZar > 0 ? Math.round(co.capex.totalZar / opexSavingZar) : null;

  return (
    <Card interactive={false} className="overflow-hidden">
      <header className="flex items-center gap-2 border-b border-border p-4">
        <Server className="h-5 w-5 text-muted-foreground" />
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-foreground">
            Co-location decision model
          </h3>
          <p className="text-xs text-muted-foreground">
            Phase 13 candidate, target 2027 Q1 if customer count crosses 40
          </p>
        </div>
      </header>

      {isPending && (
        <div className="border-b border-warning/30 bg-warning/5 px-4 py-3">
          <NotificationCard
            tone="warning"
            title="Estimates pending re-quote — do NOT use for decisions"
            body="Desk-research figures only. Re-quote required from Dell SA, Teraco/Africa DC, Tier-2 IP transit, and a remote-hands provider before this card drives a decision."
          />
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-2">
        {/* Capex column */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            One-time capex
          </p>
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Dell PowerEdge R650 + 2TB NVMe</dt>
              <dd className="font-medium tabular-nums">{fmt(co.capex.serverZar)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Spare parts + initial setup</dt>
              <dd className="font-medium tabular-nums">{fmt(co.capex.sparesSetupZar)}</dd>
            </div>
            <div className="flex justify-between border-t border-border pt-1.5">
              <dt className="font-semibold">Subtotal capex</dt>
              <dd className="font-semibold tabular-nums">{fmt(co.capex.totalZar)}</dd>
            </div>
          </dl>
        </div>

        {/* Opex column */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Monthly opex
          </p>
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Teraco / Africa DC 1U colo</dt>
              <dd className="font-medium tabular-nums">{fmt(co.opex.coloRackZar)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">IP transit (1Gbps)</dt>
              <dd className="font-medium tabular-nums">{fmt(co.opex.ipTransitZar)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-muted-foreground">Remote hands (avg)</dt>
              <dd className="font-medium tabular-nums">{fmt(co.opex.remoteHandsAvgZar)}</dd>
            </div>
            <div className="flex justify-between border-t border-border pt-1.5">
              <dt className="font-semibold">Subtotal opex</dt>
              <dd className="font-semibold tabular-nums">{fmt(co.opex.totalZarMonthly)} /mo</dd>
            </div>
          </dl>
        </div>
      </div>

      <div className="border-t border-border bg-card-subtle px-4 py-3 text-xs">
        <p className="font-semibold text-foreground">Break-even vs. XL reseller {xlReseller ? fmt(xlReseller.costZarMonthly) : ''}/mo</p>
        <p className="mt-1 text-muted-foreground">
          Opex saving: {fmt(opexSavingZar)}/mo &middot;{' '}
          {capexPaybackMonths !== null
            ? `Capex payback: ${capexPaybackMonths} months`
            : 'No opex saving — co-lo only justified by independence + customer density beyond XL'}
        </p>
        <p className="mt-1 italic text-muted-foreground">
          True break-even is at customer density where own metal beats XL plus the operational independence value (escapes domains.co.za supplier dependency).
        </p>
      </div>

      {!isPending && (
        <div className="border-t border-success/30 bg-success/5 px-4 py-2 text-xs text-muted-foreground">
          Quotes verified {verified}.
        </div>
      )}
    </Card>
  );
}
